import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { useMediaQuery } from 'react-responsive'
import { Link } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { TAB_NAMES } from 'src/constants/navigation'
import { setActiveTab } from 'src/actions/navigation'
import { PATH_HELPERS } from 'src/constants/navigation'

// Actions
import {
  fetchFavoriteListings,
  toggleFavoriteListing,
} from 'src/actions/favoriteListings'
import { addToast } from 'src/actions/toasts'

import { useFavoriteListingClickHandler } from 'src/hooks/listings'

// Selectors
import { selectClientIsGuest } from 'src/store/selectors/client'

import {
  selectFavoriteListings,
  selectFavoriteListingsLoading,
} from 'src/store/selectors/listings'

import { selectCustomerId } from 'src/store/selectors/customerProfile'

// Components
import EmptyState from 'src/Components/homeSearch/EmptyState'
import { BotHeadSpinWithMessage, If, Text, Unless } from 'src/Components'
import ListingCard from 'src/Components/homeSearch/ListingCard'

// Styles
import styles from './FavoriteListings.module.scss'

const ListView = ({ listings, customerId }) => {
  const handleFavoriteClick = useFavoriteListingClickHandler()
  const dispatch = useDispatch()

  return (
    <div className={styles.listView}>
      {listings.map((listing, position) => {
        return (
          <Link
            to={PATH_HELPERS.listing.buildPath(customerId, listing.id)}
            aria-label={listing.vector}
            key={listing.id}
          >
            <ListingCard
              listing={listing}
              className={styles.favoriteListingCard}
              favoriteClick={(e, listing) => {
                handleFavoriteClick(e, listing)
                dispatch(
                  addToast({
                    listingId: listing.id,
                    position,
                    message: 'Listing removed',
                    action: 'Undo',
                    onClose: () => {
                      dispatch(toggleFavoriteListing({ listing, position }))
                    },
                  })
                )
              }}
              isFavorite
            />
          </Link>
        )
      })}
    </div>
  )
}

const FavoriteListings = () => {
  const customerId = useSelector(selectCustomerId)
  const dispatch = useDispatch()
  const listings = useSelector(selectFavoriteListings)
  const clientIsGuest = useSelector(selectClientIsGuest)
  const loading = useSelector(selectFavoriteListingsLoading)
  const isMediumScreen = useMediaQuery({
    query: `(min-width: ${styles.mediumBreak})`,
  })

  useEffect(() => {
    dispatch(setActiveTab(TAB_NAMES.FAVORITE))
  }, [dispatch])

  useEffect(() => {
    if (!clientIsGuest) {
      dispatch(fetchFavoriteListings(customerId))
    }
  }, [dispatch, clientIsGuest, customerId])

  if (loading) {
    return (
      <BotHeadSpinWithMessage className={styles.loadingContainer}>
        <FormattedMessage
          id='BotHeadSpinWithMessage.FavoriteListings'
          defaultMessage='Loading your favorite listings...'
        />
      </BotHeadSpinWithMessage>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text
          element='h1'
          size={isMediumScreen ? 'xl' : 's'}
          align='left'
          weight='semibold'
          variant='neutralCool'
          variantCount='900'
        >
          <FormattedMessage
            id='HomeSearch.favoriteListings.heading'
            defaultMessage='Favorite listings'
          />
        </Text>
        {listings?.length > 0 && (
          <span>
            <Text
              className={styles.headerBadge}
              size='xxs'
              weight='bold'
              variant='neutralCool'
              variantCount='100'
              element='span'
            >
              {listings.length}
            </Text>
          </span>
        )}
      </div>

      <Unless condition={loading}>
        <Unless condition={isEmpty(listings)}>
          <ListView listings={listings} customerId={customerId} />
        </Unless>
        <If condition={isEmpty(listings)}>
          <EmptyState
            copy={
              <FormattedMessage
                id='HomeSearch.favoriteListings.emptyState.copy'
                defaultMessage='You have no favorite listings. Search for a city or zip, find a home and tap the heart icon.'
              />
            }
            ctaCopy={
              <FormattedMessage
                id='HomeSearch.favoriteListings.emptyState.cta'
                defaultMessage='Start search'
              />
            }
          />
        </If>
      </Unless>
    </div>
  )
}

ListView.propTypes = {
  listings: PropTypes.array,
  customerId: PropTypes.string,
}

export default FavoriteListings
